import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form, Spinner, Alert, Badge, Nav, OverlayTrigger, Tooltip, Table } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { marked } from 'marked';
import { colors } from '../../brand-colors';
import RichTextEditor from '../../components/RichTextEditor';

// Funnel Step Component
function FunnelStep({ icon, title, subtitle, isComplete, components, onClick, onEditComponent, onExportComponent, isSequence, widgetTypes, onPublish, publishingId, onToggleExpand, isExpanded }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const isSingleton = !isSequence && components.length > 0;

  // For singleton types with an existing component, click goes straight to edit
  // For sequences with components and an expand handler, toggle the panel
  const handleCardClick = () => {
    if (isSequence && components.length > 0 && onToggleExpand) {
      onToggleExpand();
    } else if (isSingleton && components.length === 1) {
      onEditComponent(components[0]);
    } else {
      onClick();
    }
  };

  return (
    <div
      className="funnel-step position-relative"
      style={{ minWidth: '140px', maxWidth: '160px' }}
      onMouseEnter={() => components.length > 0 && isSequence && !onToggleExpand && setShowDropdown(true)}
      onMouseLeave={() => setShowDropdown(false)}
    >
      <div
        onClick={handleCardClick}
        style={{
          cursor: 'pointer',
          padding: '20px 15px',
          borderRadius: '12px',
          backgroundColor: isExpanded ? '#fff8e6' : isComplete ? '#d4edda' : '#f8f9fa',
          border: isExpanded ? `2px solid ${colors.goldMid}` : isComplete ? '2px solid #28a745' : '2px dashed #dee2e6',
          textAlign: 'center',
          transition: 'all 0.2s ease',
          position: 'relative'
        }}
        className="funnel-step-box"
        onMouseOver={(e) => {
          e.currentTarget.style.transform = 'translateY(-3px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {/* Status indicator */}
        {isComplete && (
          <div
            style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              backgroundColor: '#28a745',
              color: 'white',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            ✓
          </div>
        )}

        {/* Icon */}
        <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>{icon}</div>

        {/* Title */}
        <div style={{ fontWeight: 'bold', fontSize: '0.95rem', color: '#333' }}>{title}</div>

        {/* Subtitle */}
        <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '4px' }}>{subtitle}</div>

        {/* Component count for sequences */}
        {isSequence && components.length > 0 && (
          <Badge bg="primary" className="mt-2">{components.length} email{components.length !== 1 ? 's' : ''}</Badge>
        )}

        {/* Click hint */}
        <div style={{ fontSize: '0.7rem', color: '#999', marginTop: '8px' }}>
          {isComplete ? 'Click to edit' : 'Click to create'}
        </div>
      </div>

      {/* Publish button for singleton components with content */}
      {isSingleton && onPublish && components[0]?.content && (
        <div className="text-center mt-2">
          {components[0].wordpress_page_url ? (
            <a
              href={components[0].wordpress_page_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: '0.7rem', color: '#28a745' }}
            >
              Published ✓
            </a>
          ) : (
            <Button
              variant="outline-success"
              size="sm"
              style={{ fontSize: '0.7rem', padding: '2px 10px' }}
              disabled={publishingId === components[0].id}
              onClick={(e) => { e.stopPropagation(); onPublish(components[0]); }}
            >
              {publishingId === components[0].id ? 'Publishing...' : 'Publish to WP'}
            </Button>
          )}
        </div>
      )}

      {/* Dropdown for existing components */}
      {showDropdown && components.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 100,
            minWidth: '200px',
            padding: '8px 0'
          }}
        >
          {components.map(comp => (
            <div
              key={comp.id}
              className="d-flex justify-content-between align-items-center px-3 py-2"
              style={{ borderBottom: '1px solid #f0f0f0' }}
            >
              <span className="small" style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {comp.component_type === 'lead_magnet_app' && widgetTypes?.[comp.widget_type]?.icon && (
                  <span className="me-1">{widgetTypes[comp.widget_type].icon}</span>
                )}
                {comp.name}
              </span>
              <div>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 me-2"
                  onClick={(e) => { e.stopPropagation(); onEditComponent(comp); }}
                >
                  Edit
                </Button>
                {comp.content && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0"
                    onClick={(e) => { e.stopPropagation(); onExportComponent(comp.id, comp.name); }}
                  >
                    Export
                  </Button>
                )}
              </div>
            </div>
          ))}
          <div className="px-3 pt-2">
            <Button
              variant="outline-primary"
              size="sm"
              className="w-100"
              onClick={onClick}
            >
              + Add {isSequence ? 'Another' : 'New'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Arrow Component
function FunnelArrow() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '0 8px',
        color: '#adb5bd'
      }}
    >
      <svg width="30" height="20" viewBox="0 0 30 20">
        <defs>
          <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#adb5bd" />
          </marker>
        </defs>
        <line x1="0" y1="10" x2="20" y2="10" stroke="#adb5bd" strokeWidth="2" markerEnd="url(#arrowhead)" />
      </svg>
    </div>
  );
}

function FunnelBuilder() {
  const { funnelId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [funnel, setFunnel] = useState(null);
  const [componentTypes, setComponentTypes] = useState({});
  const [widgetTypes, setWidgetTypes] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Component editor state
  const [showComponentModal, setShowComponentModal] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);
  const [componentForm, setComponentForm] = useState({
    component_type: '',
    name: '',
    content: '',
    sequence_number: null,
    widget_type: null,
    widget_config: null
  });
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishedUrl, setPublishedUrl] = useState(null);
  const [publishingComponentId, setPublishingComponentId] = useState(null);

  // Lead magnet type selection state
  const [showLeadMagnetSelector, setShowLeadMagnetSelector] = useState(false);
  const [leadMagnetStep, setLeadMagnetStep] = useState('format'); // 'format', 'widget_type', 'widget_config', 'existing'
  const [selectedWidgetType, setSelectedWidgetType] = useState(null);
  const [widgetDescription, setWidgetDescription] = useState('');
  const [generatedOptions, setGeneratedOptions] = useState([]);
  const [generatingOptions, setGeneratingOptions] = useState(false);

  // Widget code generation
  const [widgetCode, setWidgetCode] = useState('');
  const [generatingWidget, setGeneratingWidget] = useState(false);
  const [showWidgetPreview, setShowWidgetPreview] = useState(false);

  // Batch email generation & GHL sync
  const [generatingAllEmails, setGeneratingAllEmails] = useState(false);
  const [syncingToGhl, setSyncingToGhl] = useState(false);
  const [expandedSequence, setExpandedSequence] = useState(null); // 'lead_magnet_emails', 'tripwire_emails', 'post_purchase_emails'

  // Auto-generate flag — triggers AI generation when modal opens
  const [autoGenerate, setAutoGenerate] = useState(false);

  // Lead magnets state
  const [leadMagnets, setLeadMagnets] = useState([]);
  const [selectedLeadMagnetId, setSelectedLeadMagnetId] = useState('');

  // Existing lead magnet state (for "I already have one" option)
  const [existingLeadMagnetForm, setExistingLeadMagnetForm] = useState({
    url: '',
    description: ''
  });
  const [savingExistingLeadMagnet, setSavingExistingLeadMagnet] = useState(false);

  // Tripwire product selection state
  const [showTripwireSelector, setShowTripwireSelector] = useState(false);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Irresistible Offer state
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offerForm, setOfferForm] = useState({
    // Core offer
    offer_name: '',
    offer_type: '',
    offer_includes: '',
    regular_price: '',
    offer_price: '',
    offer_savings: '',
    // Bonuses, guarantee, urgency, price anchoring
    bonuses: [],
    guarantee_type: '',
    guarantee_description: '',
    urgency_type: '',
    urgency_description: '',
    price_anchoring: ''
  });
  const [newBonus, setNewBonus] = useState({ name: '', value: '', description: '' });
  const [bonusLibrary, setBonusLibrary] = useState([]);
  const [showBonusPicker, setShowBonusPicker] = useState(false);
  const [savingOffer, setSavingOffer] = useState(false);

  // Sales page pre-generation form
  const [showSalesPagePreGen, setShowSalesPagePreGen] = useState(false);
  const [salesPagePreGen, setSalesPagePreGen] = useState({
    videoUrl: '',
    testimonialSource: 'caseStudies',
    manualTestimonials: '',
    objections: '',
    customerCount: '',
  });

  // Load offer data when funnel is fetched — hydrate from BusinessData if funnel offer is empty
  useEffect(() => {
    if (funnel) {
      const funnelHasOffer = funnel.offer_name || funnel.offer_includes || funnel.offer_guarantee_type;

      if (funnelHasOffer) {
        // Use funnel's own offer data
        setOfferForm({
          offer_name: funnel.offer_name || '',
          offer_type: funnel.offer_type || '',
          offer_includes: funnel.offer_includes || '',
          regular_price: funnel.offer_regular_price || '',
          offer_price: funnel.offer_price || '',
          offer_savings: funnel.offer_savings || '',
          bonuses: funnel.offer_bonuses || [],
          guarantee_type: funnel.offer_guarantee_type || '',
          guarantee_description: funnel.offer_guarantee_description || '',
          urgency_type: funnel.offer_urgency_type || '',
          urgency_description: funnel.offer_urgency_description || '',
          price_anchoring: funnel.offer_price_anchoring || ''
        });
      } else {
        // Try to hydrate from BusinessData irresistible offer
        (async () => {
          try {
            const res = await fetch('/api/irresistible-offer/get', { credentials: 'include' });
            if (res.ok) {
              const data = await res.json();
              const offer = data.offer;
              if (offer && (offer.complete_offer || offer.guarantee || offer.bonus_stack?.length)) {
                setOfferForm({
                  offer_name: offer.offer_name || offer.headline || '',
                  offer_type: '',
                  offer_includes: offer.complete_offer || '',
                  regular_price: offer.price_anchoring?.total_value || '',
                  offer_price: '',
                  offer_savings: '',
                  bonuses: (offer.bonus_stack || []).map(b => ({
                    name: b.name || b.title || '',
                    value: b.value || '',
                    description: b.description || ''
                  })),
                  guarantee_type: offer.guarantee?.type || '',
                  guarantee_description: offer.guarantee?.description || offer.guarantee?.headline || '',
                  urgency_type: offer.urgency?.mechanism || '',
                  urgency_description: offer.urgency?.description || offer.urgency?.headline || '',
                  price_anchoring: offer.price_anchoring?.copy || ''
                });
              }
            }
          } catch (err) {
            // Silent — no existing offer is fine
          }
        })();
      }

      // Load existing lead magnet data
      setExistingLeadMagnetForm({
        url: funnel.existing_lead_magnet_url || '',
        description: funnel.existing_lead_magnet_description || ''
      });
    }
  }, [funnel]);

  useEffect(() => {
    fetchFunnel();
    fetchLeadMagnets();
  }, [funnelId]);

  // Auto-trigger AI generation when modal opens with autoGenerate flag
  useEffect(() => {
    if (autoGenerate && showComponentModal && !generating && componentForm.component_type) {
      setAutoGenerate(false);
      generateContent(componentForm.component_type);
    }
  }, [autoGenerate, showComponentModal, componentForm.component_type]);

  const fetchLeadMagnets = async () => {
    try {
      const response = await fetch('/api/lead-magnets', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setLeadMagnets(data.lead_magnets || []);
      }
    } catch (err) {
      console.error('Failed to fetch lead magnets:', err);
    }
  };

  const fetchFunnel = async () => {
    try {
      const response = await fetch(`/api/funnels/${funnelId}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setFunnel(data.funnel);
        setComponentTypes(data.component_types || {});
        setWidgetTypes(data.widget_types || {});
      } else {
        setError('Funnel not found');
      }
    } catch (err) {
      setError('Failed to load funnel');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await fetch('/api/products/get-catalog', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        // Map catalog format to simpler product format
        const productList = (data.catalog || []).map(p => ({
          id: p.id,
          name: p.name,
          description: p.notes || '',
          price: p.sale_price,
          original_price: null,
          features: [],
          position: p.position,
          purchase_type: p.purchase_type,
          is_primary_tripwire: p.is_primary_tripwire
        }));
        setProducts(productList);
      }
    } catch (err) {
      console.error('Failed to fetch products:', err);
    } finally {
      setLoadingProducts(false);
    }
  };

  const openAddComponent = (componentType) => {
    // For lead magnet types, show the format selector first
    if (componentType === 'lead_magnet' || componentType === 'lead_magnet_app') {
      setLeadMagnetStep('format');
      setSelectedWidgetType(null);
      setWidgetDescription('');
      setGeneratedOptions([]);
      setShowLeadMagnetSelector(true);
      return;
    }

    // For tripwire, show product selector
    if (componentType === 'tripwire_page') {
      fetchProducts();
      setShowTripwireSelector(true);
      return;
    }

    const typeInfo = componentTypes[componentType] || {};
    setEditingComponent(null);
    setComponentForm({
      component_type: componentType,
      name: typeInfo.name || componentType,
      content: '',
      sequence_number: typeInfo.is_sequence ? 1 : null,
      widget_type: null,
      widget_config: null
    });
    setShowComponentModal(true);

    // Auto-generate AI content for page types (not email sequences)
    const autoGenerateTypes = ['lead_magnet_landing', 'sales_page', 'upsell_page'];
    if (autoGenerateTypes.includes(componentType)) {
      setAutoGenerate(true);
    }
  };

  // Tripwire selection handlers
  const selectTripwireProduct = (product) => {
    setShowTripwireSelector(false);
    setEditingComponent(null);
    setComponentForm({
      component_type: 'tripwire_page',
      name: `${product.name} Tripwire Page`,
      content: '',
      sequence_number: null,
      widget_type: null,
      widget_config: null
    });
    setShowComponentModal(true);
    setAutoGenerate(true);
  };

  const createNewTripwire = () => {
    setShowTripwireSelector(false);
    setEditingComponent(null);
    setComponentForm({
      component_type: 'tripwire_page',
      name: 'Tripwire Page',
      content: '',
      sequence_number: null,
      widget_type: null,
      widget_config: null
    });
    setShowComponentModal(true);
    setAutoGenerate(true);
  };

  const generateTripwireContent = (product) => {
    return `# WAIT! Your Download Is Ready...
## But First, Check Out This Special One-Time Offer

---

# ${product.name}: ${product.description || 'Transform Your Results'}

**One-Time Special Price: $${product.price || '[PRICE]'}** ${product.original_price ? `(Regular $${product.original_price})` : ''}

---

## You just took the first step by downloading your free guide...

But here's the thing: Knowledge without implementation stays just that - knowledge.

## Introducing ${product.name}

${product.description || '[Describe what this product does and the transformation it provides]'}

---

## What's Included:

${product.features ? product.features.map(f => `✓ ${f}`).join('\n') : `✓ [Feature 1] - Value: $[X]
✓ [Feature 2] - Value: $[X]
✓ [Feature 3] - Value: $[X]`}

**Total Value: $${product.value || '[TOTAL]'}**
**Your Price Today: $${product.price || '[PRICE]'}**

---

## This Offer Expires When You Leave This Page

[Buy Now Button]

---

## Still Not Sure?

${product.guarantee || '[Handle top objection and include your guarantee statement]'}

---

## [No thanks, just take me to my download]`;
  };

  // Lead Magnet format selection handlers
  const selectLeadMagnetFormat = (format) => {
    if (format === 'pdf') {
      // Go directly to PDF content editor
      setShowLeadMagnetSelector(false);
      setEditingComponent(null);
      setComponentForm({
        component_type: 'lead_magnet',
        name: 'Lead Magnet Content',
        content: '',
        sequence_number: null,
        widget_type: null,
        widget_config: null
      });
      setShowComponentModal(true);
    } else if (format === 'existing') {
      // Show existing lead magnet form
      setLeadMagnetStep('existing');
    } else {
      // Show widget type selector
      setLeadMagnetStep('widget_type');
    }
  };

  // Save existing lead magnet to funnel
  const saveExistingLeadMagnet = async () => {
    // Must select a lead magnet OR provide an external URL
    if (!selectedLeadMagnetId && !existingLeadMagnetForm.url.trim()) {
      setError('Please select a lead magnet or enter an external URL');
      return;
    }

    setSavingExistingLeadMagnet(true);
    try {
      const updateData = {};

      // If a lead magnet is selected from library, use that
      if (selectedLeadMagnetId) {
        updateData.lead_magnet_id = parseInt(selectedLeadMagnetId);
      }

      // If external URL provided, save that too
      if (existingLeadMagnetForm.url.trim()) {
        updateData.existing_lead_magnet_url = existingLeadMagnetForm.url;
        updateData.existing_lead_magnet_description = existingLeadMagnetForm.description;
      }

      const response = await fetch(`/api/funnels/${funnelId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        setShowLeadMagnetSelector(false);
        setLeadMagnetStep('format'); // Reset for next time
        setSelectedLeadMagnetId(''); // Reset selection
        setSuccess('Lead magnet saved!');
        setTimeout(() => setSuccess(''), 3000);
        fetchFunnel(); // Refresh funnel data
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save lead magnet');
      }
    } catch (err) {
      setError('Error saving lead magnet');
    } finally {
      setSavingExistingLeadMagnet(false);
    }
  };

  const selectWidgetType = (widgetType) => {
    setSelectedWidgetType(widgetType);
    setLeadMagnetStep('widget_config');
  };

  const generateWidgetOptions = async () => {
    setGeneratingOptions(true);
    setError('');

    try {
      // Call API to generate 3 widget options based on business context
      const response = await fetch('/api/funnels/generate-widget-options', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          widget_type: selectedWidgetType,
          user_description: widgetDescription
        })
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedOptions(data.options || []);
      } else {
        // Fallback to placeholder options
        const widgetInfo = widgetTypes[selectedWidgetType] || {};
        setGeneratedOptions([
          {
            id: 1,
            title: `${widgetInfo.name} - Standard`,
            description: `A straightforward ${widgetInfo.name?.toLowerCase()} that calculates key metrics for your visitors and provides actionable insights.`,
            features: ['Clean, professional design', '3-5 input fields', 'Visual results chart', 'Email capture before results']
          },
          {
            id: 2,
            title: `${widgetInfo.name} - Advanced`,
            description: `A comprehensive ${widgetInfo.name?.toLowerCase()} with multiple scenarios and detailed breakdowns.`,
            features: ['Multi-step wizard', 'Comparison mode', 'PDF report option', 'Personalized recommendations']
          },
          {
            id: 3,
            title: `${widgetInfo.name} - Quick`,
            description: `A simple, fast ${widgetInfo.name?.toLowerCase()} for visitors who want quick answers.`,
            features: ['Single-page layout', '2-3 key inputs', 'Instant results', 'One-click share']
          }
        ]);
      }
    } catch (err) {
      // Fallback to placeholder options
      const widgetInfo = widgetTypes[selectedWidgetType] || {};
      setGeneratedOptions([
        {
          id: 1,
          title: `${widgetInfo.name} - Option 1`,
          description: `Standard implementation with core features.`,
          features: ['Core calculation', 'Clean design', 'Email capture']
        },
        {
          id: 2,
          title: `${widgetInfo.name} - Option 2`,
          description: `Advanced implementation with additional features.`,
          features: ['Advanced calculations', 'Multiple scenarios', 'PDF export']
        },
        {
          id: 3,
          title: `${widgetInfo.name} - Option 3`,
          description: `Simplified quick version.`,
          features: ['Fast results', 'Minimal inputs', 'Easy sharing']
        }
      ]);
    } finally {
      setGeneratingOptions(false);
    }
  };

  const selectWidgetOption = (option) => {
    const widgetInfo = widgetTypes[selectedWidgetType] || {};
    setShowLeadMagnetSelector(false);
    setEditingComponent(null);
    setComponentForm({
      component_type: 'lead_magnet_app',
      name: option.title || widgetInfo.name,
      content: widgetDescription || '',
      sequence_number: null,
      widget_type: selectedWidgetType,
      widget_config: JSON.stringify({
        option_id: option.id,
        option_title: option.title,
        option_description: option.description,
        features: option.features,
        user_description: widgetDescription
      })
    });
    setWidgetCode('');
    setShowComponentModal(true);
  };

  const createCustomWidget = () => {
    const widgetInfo = widgetTypes[selectedWidgetType] || {};
    setShowLeadMagnetSelector(false);
    setEditingComponent(null);
    setComponentForm({
      component_type: 'lead_magnet_app',
      name: widgetInfo.name,
      content: widgetDescription,
      sequence_number: null,
      widget_type: selectedWidgetType,
      widget_config: JSON.stringify({
        custom: true,
        user_description: widgetDescription
      })
    });
    setWidgetCode('');
    setShowComponentModal(true);
  };

  const openEditComponent = (component) => {
    setEditingComponent(component);
    setWidgetCode(component.widget_code || '');
    setComponentForm({
      component_type: component.component_type,
      name: component.name || '',
      content: component.content || '',
      sequence_number: component.sequence_number,
      widget_type: component.widget_type || null,
      widget_config: component.widget_config || null
    });
    setShowComponentModal(true);
  };

  const saveComponent = async () => {
    setSaving(true);
    try {
      const url = editingComponent
        ? `/api/funnels/${funnelId}/components/${editingComponent.id}`
        : `/api/funnels/${funnelId}/components`;
      const method = editingComponent ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...componentForm, widget_code: widgetCode || undefined })
      });

      if (response.ok) {
        setShowComponentModal(false);
        setSuccess(editingComponent ? 'Component updated!' : 'Component added!');
        setTimeout(() => setSuccess(''), 3000);
        fetchFunnel();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save component');
      }
    } catch (err) {
      setError('Error saving component');
    } finally {
      setSaving(false);
    }
  };

  const publishComponent = async () => {
    if (!editingComponent?.id) {
      setError('Please save the component before publishing');
      return;
    }

    setPublishing(true);
    setPublishedUrl(null);
    try {
      const response = await fetch(`/api/publish/funnel-component/${editingComponent.id}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      if (data.success) {
        setPublishedUrl(data.url);
        setSuccess(`Published! View at: ${data.url}`);
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(data.error || 'Failed to publish');
      }
    } catch (err) {
      setError('Error publishing to WordPress');
    } finally {
      setPublishing(false);
    }
  };

  const quickPublish = async (component) => {
    setPublishingComponentId(component.id);
    try {
      const response = await fetch(`/api/publish/funnel-component/${component.id}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(`Published! View at: ${data.url}`);
        setTimeout(() => setSuccess(''), 5000);
        fetchFunnel(); // Refresh to show published status
      } else {
        setError(data.error || 'Failed to publish');
      }
    } catch (err) {
      setError('Error publishing to WordPress');
    } finally {
      setPublishingComponentId(null);
    }
  };

  const deleteComponent = async (componentId) => {
    if (!window.confirm('Are you sure you want to delete this component?')) {
      return;
    }

    try {
      const response = await fetch(`/api/funnels/${funnelId}/components/${componentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setSuccess('Component deleted');
        setTimeout(() => setSuccess(''), 3000);
        if (showComponentModal) {
          setShowComponentModal(false);
        }
        fetchFunnel();
      }
    } catch (err) {
      setError('Error deleting component');
    }
  };

  const exportComponent = async (componentId, componentName) => {
    try {
      const response = await fetch(`/api/funnels/${funnelId}/components/${componentId}/export`, {
        credentials: 'include'
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${componentName.replace(/[^a-z0-9]/gi, '_')}.txt`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to export');
      }
    } catch (err) {
      setError('Error exporting component');
    }
  };

  const generateContent = async (overrideComponentType = null, extraParams = {}) => {
    const compType = overrideComponentType || componentForm.component_type;

    // For sales pages, show the pre-generation form first
    if (compType === 'sales_page' && !extraParams._confirmed) {
      setShowSalesPagePreGen(true);
      return;
    }

    setGenerating(true);
    setError('');

    try {
      const response = await fetch(`/api/funnels/${funnelId}/generate-content`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          component_type: compType,
          sequence_number: componentForm.sequence_number || 1,
          ...extraParams,
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.content) {
          // Convert markdown to HTML for the rich text editor
          const htmlContent = marked(data.content, { breaks: true, gfm: true });
          setComponentForm({ ...componentForm, content: htmlContent });
          setSuccess('Content generated! Review and customize as needed.');
          setTimeout(() => setSuccess(''), 3000);
        } else {
          // Fall back to placeholder if API fails
          const placeholderContent = getPlaceholderContent(componentForm.component_type);
          setComponentForm({ ...componentForm, content: placeholderContent });
          setSuccess('Using template - customize for your business.');
          setTimeout(() => setSuccess(''), 3000);
        }
      } else {
        // Fall back to placeholder on error
        const placeholderContent = getPlaceholderContent(componentForm.component_type);
        setComponentForm({ ...componentForm, content: placeholderContent });
        setError('AI generation unavailable - using template instead');
      }
    } catch (err) {
      // Fall back to placeholder on network error
      const placeholderContent = getPlaceholderContent(componentForm.component_type);
      setComponentForm({ ...componentForm, content: placeholderContent });
      setError('Connection error - using template instead');
    } finally {
      setGenerating(false);
    }
  };

  const handleSalesPageGenerate = () => {
    setShowSalesPagePreGen(false);
    generateContent('sales_page', {
      _confirmed: true,
      video_url: salesPagePreGen.videoUrl,
      testimonial_source: salesPagePreGen.testimonialSource,
      manual_testimonials: salesPagePreGen.manualTestimonials,
      objections: salesPagePreGen.objections,
      customer_count: salesPagePreGen.customerCount,
    });
  };

  const SUPPORTED_WIDGET_TYPES = ['roi_calculator', 'assessment', 'break_even'];

  const generateWidgetCode = async () => {
    setGeneratingWidget(true);
    setError('');

    try {
      const response = await fetch(`/api/funnels/${funnelId}/generate-content`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          component_type: 'lead_magnet_app',
          widget_type: componentForm.widget_type,
          widget_config: componentForm.widget_config,
          content: componentForm.content
        })
      });

      const data = await response.json();
      if (response.ok && data.success && data.widget_code) {
        setWidgetCode(data.widget_code);
        setSuccess('Widget code generated! Preview it or copy the embed code.');
        setTimeout(() => setSuccess(''), 4000);
      } else {
        setError(data.error || 'Failed to generate widget code. Please try again.');
      }
    } catch (err) {
      setError('Connection error generating widget code.');
    } finally {
      setGeneratingWidget(false);
    }
  };

  // Generate all 6 nurture emails at once with UTM tracking
  const generateAllEmails = async (syncToGhl = false) => {
    setGeneratingAllEmails(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`/api/funnels/${funnelId}/generate-all-emails`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sync_to_ghl: syncToGhl })
      });

      const data = await response.json();
      if (response.ok && data.success) {
        // Refresh funnel data to show new emails
        fetchFunnel();
        const ghlMsg = data.ghl_synced ? ' and synced to GHL' : '';
        setSuccess(`Created ${data.generated.length} nurture emails with UTM tracking${ghlMsg}!`);
        if (data.failed.length > 0) {
          setError(`Failed to generate emails: ${data.failed.join(', ')}`);
        }
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(data.error || 'Failed to generate email sequence.');
      }
    } catch (err) {
      setError('Connection error generating email sequence.');
    } finally {
      setGeneratingAllEmails(false);
    }
  };

  // Generate all emails for a sequence type (tripwire or post-purchase)
  const [generatingSequence, setGeneratingSequence] = useState(null); // tracks which sequence type is generating

  const generateSequenceEmails = async (sequenceType) => {
    setGeneratingSequence(sequenceType);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`/api/funnels/${funnelId}/generate-all-sequence-emails`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence_type: sequenceType })
      });

      const data = await response.json();
      if (response.ok && data.success) {
        fetchFunnel();
        const label = sequenceType === 'tripwire_emails' ? 'tripwire follow-up' : 'post-purchase';
        setSuccess(`Created ${data.generated.length} ${label} emails with UTM tracking!`);
        if (data.failed.length > 0) {
          setError(`Failed to generate emails: ${data.failed.join(', ')}`);
        }
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(data.error || 'Failed to generate email sequence.');
      }
    } catch (err) {
      setError('Connection error generating email sequence.');
    } finally {
      setGeneratingSequence(null);
    }
  };

  // Sync existing emails to GHL
  const syncEmailsToGhl = async (sequenceType = 'all') => {
    setSyncingToGhl(true);
    setError('');

    try {
      const response = await fetch(`/api/funnels/${funnelId}/sync-emails`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sequence_type: sequenceType })
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setSuccess(data.message || 'Emails synced to GHL!');
        setTimeout(() => setSuccess(''), 4000);
      } else {
        setError(data.error || 'Failed to sync emails to GHL.');
      }
    } catch (err) {
      setError('Connection error syncing to GHL.');
    } finally {
      setSyncingToGhl(false);
    }
  };

  const generateUpsellPageContent = (product) => {
    return `# You Made a Great Decision...
## Now Take It to the Next Level

---

Congratulations on your purchase! You've just taken an important step toward [RESULT].

But here's what I've learned from working with thousands of customers just like you...

**The ones who see the BEST results are the ones who [NEXT LEVEL ACTION].**

---

# Introducing ${product.name}

${product.description || '[Describe what this product does and how it builds on what they just bought]'}

## Why This Is Perfect for You RIGHT NOW

You've already shown you're serious about [OUTCOME] by purchasing [PREVIOUS PRODUCT].

${product.name} is the natural next step because:

✓ **[Benefit 1]** - Building on what you just learned
✓ **[Benefit 2]** - Taking your results further
✓ **[Benefit 3]** - Accelerating your progress

---

## What's Included:

${product.features ? product.features.map(f => `✓ ${f}`).join('\n') : `✓ [Feature 1] - Value: $[X]
✓ [Feature 2] - Value: $[X]
✓ [Feature 3] - Value: $[X]`}

---

## Special Offer for Recent Customers

Because you just purchased [PREVIOUS PRODUCT], you're getting exclusive access to this offer:

**Regular Price:** $[REGULAR PRICE]
**Your Price Today:** $${product.price || '[PRICE]'}

---

## The Choice Is Yours

**Option 1:** Continue with what you have. You'll still see results, just at a slower pace.

**Option 2:** Add ${product.name} and fast-track your way to [BIGGER RESULT].

[YES, I WANT TO UPGRADE]

---

## 100% Risk-Free

[Your guarantee - same as your other products or better]

---

[No thanks, I'll stick with my current purchase]`;
  };

  const getPlaceholderContent = (type) => {
    switch (type) {
      case 'lead_magnet':
        return `# [Your Lead Magnet Title]

## Introduction
[Hook your reader with a compelling opening that addresses their main pain point]

## The Problem
[Describe the problem your audience faces in detail]

## The Solution
[Introduce your framework/method/solution]

## Step 1: [First Key Point]
[Detailed explanation with actionable advice]

## Step 2: [Second Key Point]
[Detailed explanation with actionable advice]

## Step 3: [Third Key Point]
[Detailed explanation with actionable advice]

## Quick Win
[Give them something they can implement immediately]

## Conclusion & Next Steps
[Recap and transition to your paid offer]

---
Ready to take this further? [CTA for your tripwire or main offer]`;

      case 'lead_magnet_app':
        return `# Interactive Widget Configuration

## Widget Description
${componentForm.content || '[Describe what this widget should do for your visitors]'}

## User Inputs
- [Input Field 1]: [Description]
- [Input Field 2]: [Description]
- [Input Field 3]: [Description]

## Calculation Logic
[How should the widget calculate results?]

## Results Display
- [Result Metric 1]: [How to present]
- [Result Metric 2]: [How to present]
- [Recommendations]: [Based on results]

## Call to Action
[What should users do after seeing their results?]

---
Note: The actual widget code will be generated based on this configuration.`;

      case 'lead_magnet_landing':
        return `# PRE-HEADLINE
"For [Target Audience] Who Want [Desired Outcome]"

# HEADLINE
[Big Promise] Without [Pain Point] - Free Guide

# SUBHEADLINE
Discover the [X]-step system to [specific benefit] even if [common objection]

---

## What You'll Learn:

✓ [Benefit 1] - [Brief explanation]
✓ [Benefit 2] - [Brief explanation]
✓ [Benefit 3] - [Brief explanation]
✓ [Benefit 4] - [Brief explanation]

---

## About the Author
[Brief credibility statement - who you are and why they should trust you]

---

## GET INSTANT ACCESS

[Email opt-in form]

Your privacy is protected. We'll never spam you.

---

## What Others Are Saying
"[Testimonial 1]" - [Name, Title]
"[Testimonial 2]" - [Name, Title]`;

      case 'lead_magnet_emails':
        return `# EMAIL ${componentForm.sequence_number || 1}: [Email Theme]

**Subject Line:** [Compelling subject line]
**Preview Text:** [First line preview]

---

Hi [First Name],

[Opening hook - acknowledge where they are in their journey]

[Main content - provide value related to your lead magnet]

[Key insight or tip they can use today]

[Soft transition toward your paid offer - optional for early emails]

[Clear CTA]

Talk soon,
[Your Name]

P.S. [Curiosity hook or bonus tip]

---
**Send timing:** Day [X] after opt-in
**Goal:** [What this email should accomplish]`;

      case 'tripwire_page':
        return `# WAIT! Your Download Is Ready...
## But First, Check Out This Special One-Time Offer

---

# [Product Name]: [Big Promise]

**One-Time Special Price: $[X]** (Regular $[Y])

---

## You just took the first step by downloading [Lead Magnet Name]...

But here's the thing: [Bridge from free content to paid offer]

## Introducing [Product Name]

[What it is and what it does for them]

---

## What's Included:

✓ [Component 1] - Value: $[X]
✓ [Component 2] - Value: $[X]
✓ [Component 3] - Value: $[X]

**Total Value: $[X]**
**Your Price Today: $[X]**

---

## This Offer Expires When You Leave This Page

[Buy Now Button]

---

## Still Not Sure?

[Handle top objection]

[Guarantee statement]

---

## [No thanks, just take me to my download]`;

      case 'tripwire_emails':
        return `# TRIPWIRE EMAIL ${componentForm.sequence_number || 1}

**Subject Line:** [Compelling subject about the offer]
**Preview Text:** [Create curiosity]

---

Hi [First Name],

[Reference their recent download/action]

[Introduce the problem the tripwire solves]

[Present the tripwire offer]

[Key benefit 1]
[Key benefit 2]
[Key benefit 3]

[Urgency element - if applicable]

[Clear CTA with link]

[Your Name]

P.S. [Final push - urgency or bonus]

---
**Send timing:** [X hours/days after opt-in]
**Goal:** Convert to tripwire purchase`;

      case 'sales_page':
        return `# PRE-HEADLINE
"Finally... A [Solution Type] That Actually Works for [Target Audience]"

# HEADLINE
[Big Promise] In [Timeframe] Without [Main Pain Point]

# SUBHEADLINE
The proven [X]-step system that [specific result] - guaranteed.

---

## Dear [Target Audience],

[Opening story or hook that creates connection]

## The Problem
[Agitate the pain - make them feel understood]

## The Failed Solutions
[What they've tried that didn't work]

## The Discovery
[How you found/created the solution]

## Introducing [Product Name]

[What it is and the transformation it provides]

---

## Here's What You Get:

### Module 1: [Name]
[Description and benefit]

### Module 2: [Name]
[Description and benefit]

### Module 3: [Name]
[Description and benefit]

**PLUS These Bonuses:**

🎁 Bonus 1: [Name] (Value: $[X])
🎁 Bonus 2: [Name] (Value: $[X])

---

## Total Value: $[X]

## Your Investment Today: $[Price]

[Buy Now Button]

---

## Our Guarantee
[Risk reversal - money back guarantee details]

---

## Frequently Asked Questions

**Q: [Common question 1]**
A: [Answer]

**Q: [Common question 2]**
A: [Answer]

---

## Ready to [Transformation]?

[Final CTA]

[Buy Now Button]`;

      case 'post_purchase_emails':
        return `# POST-PURCHASE EMAIL ${componentForm.sequence_number || 1}: [Email Theme]

**Subject Line:** [Compelling subject - congratulate & excite them]
**Preview Text:** [First line preview]

---

Hi [First Name],

${componentForm.sequence_number === 1 ? `🎉 Congratulations on your purchase of [Product Name]!

You've just made an incredible investment in [outcome/transformation].

Here's what happens next:

1. **Right Now:** [Immediate access instructions]
2. **Within 24 Hours:** [What they should do first]
3. **This Week:** [First milestone to aim for]

**Your Login Details:**
[Access link or instructions]

If you have any questions, just reply to this email - I read every one.` :
componentForm.sequence_number === 2 ? `Just checking in - have you had a chance to [first action] yet?

If not, no worries! Here's a quick win you can implement in just 5 minutes:

[Quick tip or action step]

Many of our most successful customers started with exactly this.` :
`[Main content - continue building relationship and ensuring success]

[Key insight or advanced tip]

[Reminder of resources available to them]`}

Talk soon,
[Your Name]

P.S. [Bonus tip, community invite, or next steps preview]

---
**Send timing:** Day [X] after purchase
**Goal:** [What this email should accomplish - onboarding, engagement, upsell prep]`;

      case 'upsell_page':
        return `# You Made a Great Decision...
## Now Take It to the Next Level

---

Congratulations on your purchase! You've just taken an important step toward [RESULT].

But here's what I've learned from working with thousands of customers just like you...

**The ones who see the BEST results are the ones who [NEXT LEVEL ACTION].**

---

# Introducing [UPSELL PRODUCT NAME]

[Describe what this product does and how it builds on what they just bought]

## Why This Is Perfect for You RIGHT NOW

You've already shown you're serious about [OUTCOME] by purchasing [PREVIOUS PRODUCT].

[UPSELL PRODUCT] is the natural next step because:

✓ **[Benefit 1]** - Building on what you just learned
✓ **[Benefit 2]** - Taking your results further
✓ **[Benefit 3]** - Accelerating your progress

---

## What's Included:

✓ [Feature 1] - Value: $[X]
✓ [Feature 2] - Value: $[X]
✓ [Feature 3] - Value: $[X]

---

## Special Offer for Recent Customers

Because you just purchased [PREVIOUS PRODUCT], you're getting exclusive access:

**Regular Price:** $[REGULAR]
**Your Price Today:** $[UPSELL PRICE]

---

[YES, I WANT TO UPGRADE]

---

[No thanks, I'll stick with my current purchase]`;

      default:
        return `# [Component Title]

[Your content here]`;
    }
  };

  // Irresistible Offer handlers
  const fetchBonusLibrary = async () => {
    try {
      const res = await fetch('/api/bonuses', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setBonusLibrary(data.bonuses || []);
      }
    } catch (err) {
      console.error('Failed to fetch bonus library:', err);
    }
  };

  const addBonusFromLibrary = (libraryBonus) => {
    const already = offerForm.bonuses.some(b => b.name === libraryBonus.name);
    if (!already) {
      setOfferForm({
        ...offerForm,
        bonuses: [...offerForm.bonuses, {
          name: libraryBonus.name,
          value: libraryBonus.perceived_value ? String(libraryBonus.perceived_value) : '',
          description: libraryBonus.description || '',
        }]
      });
    }
    setShowBonusPicker(false);
  };

  const addBonus = () => {
    if (newBonus.name.trim()) {
      setOfferForm({
        ...offerForm,
        bonuses: [...offerForm.bonuses, { ...newBonus }]
      });
      setNewBonus({ name: '', value: '', description: '' });
    }
  };

  const removeBonus = (index) => {
    setOfferForm({
      ...offerForm,
      bonuses: offerForm.bonuses.filter((_, i) => i !== index)
    });
  };

  const saveOffer = async () => {
    setSavingOffer(true);
    try {
      const response = await fetch(`/api/funnels/${funnelId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // Core offer
          offer_name: offerForm.offer_name,
          offer_type: offerForm.offer_type,
          offer_includes: offerForm.offer_includes,
          offer_regular_price: offerForm.regular_price,
          offer_price: offerForm.offer_price,
          offer_savings: offerForm.offer_savings,
          // Bonuses, guarantee, urgency, price anchoring
          offer_bonuses: offerForm.bonuses,
          offer_guarantee_type: offerForm.guarantee_type,
          offer_guarantee_description: offerForm.guarantee_description,
          offer_urgency_type: offerForm.urgency_type,
          offer_urgency_description: offerForm.urgency_description,
          offer_price_anchoring: offerForm.price_anchoring
        })
      });

      if (response.ok) {
        setShowOfferModal(false);
        setSuccess('Irresistible Offer saved!');
        setTimeout(() => setSuccess(''), 3000);
        fetchFunnel(); // Refresh funnel data
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save offer');
      }
    } catch (err) {
      setError('Error saving offer');
    } finally {
      setSavingOffer(false);
    }
  };

  const hasOfferConfigured = () => {
    return offerForm.offer_name ||
           offerForm.offer_includes ||
           offerForm.bonuses.length > 0 ||
           offerForm.guarantee_type ||
           offerForm.urgency_type;
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" style={{ color: colors.goldMid }} />
        <p className="mt-3">Loading funnel...</p>
      </Container>
    );
  }

  if (!funnel) {
    return (
      <Container className="py-4">
        <Alert variant="danger">Funnel not found</Alert>
        <Button variant="secondary" onClick={() => navigate('/command-center/campaign-hq/funnel-management')}>
          Back to Funnels
        </Button>
      </Container>
    );
  }

  // Group components by type
  const componentsByType = {};
  (funnel.components || []).forEach(comp => {
    if (!componentsByType[comp.component_type]) {
      componentsByType[comp.component_type] = [];
    }
    componentsByType[comp.component_type].push(comp);
  });

  return (
    <Container fluid className="py-4 px-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Button
            variant="link"
            className="p-0 mb-2"
            onClick={() => navigate('/command-center/campaign-hq/funnel-management')}
          >
            ← Back to Funnels
          </Button>
          <h1>{funnel.name}</h1>
          {funnel.description && <p className="text-muted mb-0">{funnel.description}</p>}
        </div>
        <Badge bg={funnel.status === 'active' ? 'success' : 'secondary'} className="fs-6">
          {funnel.status}
        </Badge>
      </div>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {/* Visual Funnel Flow Diagram */}
      <Card className="shadow-sm mb-4">
        <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <h5 className="mb-0">Your Funnel Flow</h5>
        </Card.Header>
        <Card.Body className="py-4">
          {/* Funnel Flow Diagram */}
          <div className="funnel-flow d-flex align-items-center justify-content-center flex-wrap" style={{ gap: '0' }}>

            {/* Step 1: Lead Magnet */}
            <FunnelStep
              icon="🎁"
              title="Lead Magnet"
              subtitle={funnel?.existing_lead_magnet_url ? "Linked external" : "Free value offer"}
              isComplete={componentsByType['lead_magnet']?.length > 0 || componentsByType['lead_magnet_app']?.length > 0 || !!funnel?.existing_lead_magnet_url}
              components={[...(componentsByType['lead_magnet'] || []), ...(componentsByType['lead_magnet_app'] || [])]}
              onClick={() => openAddComponent('lead_magnet')}
              onEditComponent={openEditComponent}
              onExportComponent={exportComponent}
              widgetTypes={widgetTypes}
            />

            <FunnelArrow />

            {/* Step 2: Landing Page */}
            <FunnelStep
              icon="📄"
              title="Landing Page"
              subtitle="Capture emails"
              isComplete={componentsByType['lead_magnet_landing']?.length > 0}
              components={componentsByType['lead_magnet_landing'] || []}
              onClick={() => openAddComponent('lead_magnet_landing')}
              onEditComponent={openEditComponent}
              onExportComponent={exportComponent}
              onPublish={quickPublish}
              publishingId={publishingComponentId}
            />

            <FunnelArrow />

            {/* Step 3: Nurture Emails */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <FunnelStep
                icon="📧"
                title="Nurture Emails"
                subtitle="Build relationship"
                isComplete={componentsByType['lead_magnet_emails']?.length > 0}
                components={componentsByType['lead_magnet_emails'] || []}
                onClick={() => openAddComponent('lead_magnet_emails')}
                onEditComponent={openEditComponent}
                onExportComponent={exportComponent}
                isSequence={true}
                onToggleExpand={() => setExpandedSequence(expandedSequence === 'lead_magnet_emails' ? null : 'lead_magnet_emails')}
                isExpanded={expandedSequence === 'lead_magnet_emails'}
              />
              <div className="text-center mt-2" style={{ maxWidth: '160px' }}>
                {(componentsByType['lead_magnet_emails']?.length || 0) === 0 ? (
                  <Button
                    variant="outline-success"
                    size="sm"
                    style={{ fontSize: '0.65rem', padding: '3px 8px', lineHeight: '1.3' }}
                    disabled={generatingAllEmails}
                    onClick={() => generateAllEmails(false)}
                  >
                    {generatingAllEmails ? (
                      <>
                        <Spinner animation="border" size="sm" style={{ width: '10px', height: '10px' }} className="me-1" />
                        Creating...
                      </>
                    ) : (
                      'Auto-Create Full Sequence'
                    )}
                  </Button>
                ) : (
                  <div className="d-flex flex-column gap-1">
                    <Button
                      variant="outline-warning"
                      size="sm"
                      style={{ fontSize: '0.6rem', padding: '2px 6px' }}
                      disabled={generatingAllEmails}
                      onClick={() => {
                        if (window.confirm('This will regenerate all 6 emails, replacing the existing ones. Continue?')) {
                          generateAllEmails(false);
                        }
                      }}
                    >
                      {generatingAllEmails ? 'Regenerating...' : 'Regenerate All'}
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      style={{ fontSize: '0.6rem', padding: '2px 6px' }}
                      disabled={syncingToGhl}
                      onClick={syncEmailsToGhl}
                    >
                      {syncingToGhl ? 'Syncing...' : 'Sync to GHL'}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <FunnelArrow />

            {/* Step 4: Tripwire */}
            <FunnelStep
              icon="💰"
              title="Tripwire"
              subtitle="Low-ticket offer"
              isComplete={componentsByType['tripwire_page']?.length > 0}
              components={componentsByType['tripwire_page'] || []}
              onClick={() => openAddComponent('tripwire_page')}
              onEditComponent={openEditComponent}
              onExportComponent={exportComponent}
              onPublish={quickPublish}
              publishingId={publishingComponentId}
            />

            <FunnelArrow />

            {/* Step 5: Tripwire Emails */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <FunnelStep
                icon="📨"
                title="Follow-up Emails"
                subtitle="Convert buyers"
                isComplete={componentsByType['tripwire_emails']?.length > 0}
                components={componentsByType['tripwire_emails'] || []}
                onClick={() => openAddComponent('tripwire_emails')}
                onEditComponent={openEditComponent}
                onExportComponent={exportComponent}
                isSequence={true}
                onToggleExpand={() => setExpandedSequence(expandedSequence === 'tripwire_emails' ? null : 'tripwire_emails')}
                isExpanded={expandedSequence === 'tripwire_emails'}
              />
              <div className="text-center mt-2" style={{ maxWidth: '160px' }}>
                {(componentsByType['tripwire_emails']?.length || 0) === 0 ? (
                  <Button
                    variant="outline-success"
                    size="sm"
                    style={{ fontSize: '0.65rem', padding: '3px 8px', lineHeight: '1.3' }}
                    disabled={generatingSequence === 'tripwire_emails'}
                    onClick={() => generateSequenceEmails('tripwire_emails')}
                  >
                    {generatingSequence === 'tripwire_emails' ? (
                      <>
                        <Spinner animation="border" size="sm" style={{ width: '10px', height: '10px' }} className="me-1" />
                        Creating...
                      </>
                    ) : (
                      'Auto-Create Sequence'
                    )}
                  </Button>
                ) : (
                  <Button
                    variant="outline-warning"
                    size="sm"
                    style={{ fontSize: '0.6rem', padding: '2px 6px' }}
                    disabled={generatingSequence === 'tripwire_emails'}
                    onClick={() => {
                      if (window.confirm('This will regenerate all tripwire follow-up emails. Continue?')) {
                        generateSequenceEmails('tripwire_emails');
                      }
                    }}
                  >
                    {generatingSequence === 'tripwire_emails' ? 'Regenerating...' : 'Regenerate All'}
                  </Button>
                )}
              </div>
            </div>

            <FunnelArrow />

            {/* Step 6: Sales Page */}
            <FunnelStep
              icon="🎯"
              title="Sales Page"
              subtitle="Core offer"
              isComplete={componentsByType['sales_page']?.length > 0}
              components={componentsByType['sales_page'] || []}
              onClick={() => openAddComponent('sales_page')}
              onEditComponent={openEditComponent}
              onExportComponent={exportComponent}
              onPublish={quickPublish}
              publishingId={publishingComponentId}
            />

            <FunnelArrow />

            {/* Step 7: Post-Purchase Emails */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <FunnelStep
                icon="🎉"
                title="Onboarding"
                subtitle="Post-purchase"
                isComplete={componentsByType['post_purchase_emails']?.length > 0}
                components={componentsByType['post_purchase_emails'] || []}
                onClick={() => openAddComponent('post_purchase_emails')}
                onEditComponent={openEditComponent}
                onExportComponent={exportComponent}
                isSequence={true}
                onToggleExpand={() => setExpandedSequence(expandedSequence === 'post_purchase_emails' ? null : 'post_purchase_emails')}
                isExpanded={expandedSequence === 'post_purchase_emails'}
              />
              <div className="text-center mt-2" style={{ maxWidth: '160px' }}>
                {(componentsByType['post_purchase_emails']?.length || 0) === 0 ? (
                  <Button
                    variant="outline-success"
                    size="sm"
                    style={{ fontSize: '0.65rem', padding: '3px 8px', lineHeight: '1.3' }}
                    disabled={generatingSequence === 'post_purchase_emails'}
                    onClick={() => generateSequenceEmails('post_purchase_emails')}
                  >
                    {generatingSequence === 'post_purchase_emails' ? (
                      <>
                        <Spinner animation="border" size="sm" style={{ width: '10px', height: '10px' }} className="me-1" />
                        Creating...
                      </>
                    ) : (
                      'Auto-Create Sequence'
                    )}
                  </Button>
                ) : (
                  <Button
                    variant="outline-warning"
                    size="sm"
                    style={{ fontSize: '0.6rem', padding: '2px 6px' }}
                    disabled={generatingSequence === 'post_purchase_emails'}
                    onClick={() => {
                      if (window.confirm('This will regenerate all post-purchase emails. Continue?')) {
                        generateSequenceEmails('post_purchase_emails');
                      }
                    }}
                  >
                    {generatingSequence === 'post_purchase_emails' ? 'Regenerating...' : 'Regenerate All'}
                  </Button>
                )}
              </div>
            </div>

          </div>

          {/* Email Sequence Panel */}
          {expandedSequence && (componentsByType[expandedSequence]?.length || 0) > 0 && (() => {
            const SEQUENCE_META = {
              lead_magnet_emails: { label: 'Nurture Emails', icon: '📧', timingDays: [0, 2, 4, 6, 8, 10], timingLabel: 'Day' },
              tripwire_emails: { label: 'Follow-up Emails', icon: '📨', timingDays: [0, 1, 3, 5, 7], timingLabel: 'Day' },
              post_purchase_emails: { label: 'Onboarding Emails', icon: '🎉', timingDays: [1, 2, 3, 4, 5, 6], timingLabel: 'Day' }
            };
            const meta = SEQUENCE_META[expandedSequence];
            const emails = (componentsByType[expandedSequence] || []).sort((a, b) => (a.sequence_number || 0) - (b.sequence_number || 0));
            const isOnboarding = expandedSequence === 'post_purchase_emails';
            const upsellProduct = funnel?.product;

            return (
              <div className="mt-3 p-3 rounded" style={{ backgroundColor: '#fff8e6', border: `1px solid ${colors.goldMid}` }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">
                    <span className="me-2">{meta.icon}</span>
                    {meta.label}
                    <Badge bg="secondary" className="ms-2">{emails.length}</Badge>
                  </h6>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setExpandedSequence(null)}
                  >
                    Close
                  </Button>
                </div>

                {isOnboarding && upsellProduct && (
                  <Alert variant="light" className="py-2 small mb-3" style={{ borderColor: colors.goldLight }}>
                    <strong>Upsell target:</strong> {upsellProduct.name}
                    {upsellProduct.sale_price && <span className="ms-1">(${parseFloat(upsellProduct.sale_price).toLocaleString()})</span>}
                  </Alert>
                )}

                <Table hover size="sm" className="mb-0 bg-white" style={{ borderRadius: '8px', overflow: 'hidden' }}>
                  <thead>
                    <tr style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                      <th style={{ width: '100px', padding: '8px 12px' }}>Timing</th>
                      <th style={{ padding: '8px 12px' }}>Subject</th>
                      <th style={{ width: '100px', padding: '8px 12px', textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emails.map((comp, idx) => {
                      const day = meta.timingDays[idx] !== undefined ? meta.timingDays[idx] : (idx + 1) * 2;
                      // Extract subject from name (format: "Email N: Subject (Day X)")
                      const nameMatch = comp.name?.match(/Email \d+:\s*(.+?)(?:\s*\(Day|\s*$)/);
                      const displayName = nameMatch ? nameMatch[1] : comp.name;
                      // Get a body preview - strip HTML tags
                      const bodyPreview = comp.content ? comp.content.replace(/<[^>]+>/g, '').substring(0, 80) : '';

                      return (
                        <tr key={comp.id}>
                          <td style={{ padding: '8px 12px', verticalAlign: 'middle' }}>
                            <Badge bg={day === 0 ? 'success' : 'secondary'} style={{ fontSize: '0.75rem' }}>
                              {day === 0 ? 'Immediate' : `${meta.timingLabel} ${day}`}
                            </Badge>
                          </td>
                          <td style={{ padding: '8px 12px' }}>
                            <strong style={{ fontSize: '0.9rem' }}>{displayName}</strong>
                            {bodyPreview && (
                              <div className="text-muted small" style={{ maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {bodyPreview}...
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', verticalAlign: 'middle' }}>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="me-1"
                              style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                              onClick={() => openEditComponent(comp)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                              onClick={() => deleteComponent(comp.id)}
                            >
                              &times;
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            );
          })()}

          {/* Progress Summary */}
          <div className="mt-4 pt-4 border-top text-center">
            <div className="d-flex justify-content-center gap-4">
              <div>
                <span className="text-success fs-4 fw-bold">
                  {Object.values(componentsByType).filter(arr => arr && arr.length > 0).length}
                </span>
                <span className="text-muted ms-1">/ 7 steps complete</span>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Product & Upgrade Path Management */}
      <Card className="shadow-sm mb-4">
        <Card.Header style={{ backgroundColor: colors.greyLightest }}>
          <h5 className="mb-0">Product Foundation</h5>
        </Card.Header>
        <Card.Body>
          <p className="text-muted mb-3">
            Your funnel is built on your product catalog and upgrade paths. Make sure these are up to date.
          </p>

          <Row>
            <Col md={6} className="mb-3 mb-md-0">
              <Card className="h-100 border">
                <Card.Body className="d-flex align-items-center">
                  <div style={{ fontSize: '2rem', marginRight: '1rem' }}>📦</div>
                  <div className="flex-grow-1">
                    <h6 className="mb-1">Product Catalog</h6>
                    <p className="text-muted small mb-2">Manage your products, pricing, and value ladder</p>
                    <Button
                      variant="outline-dark"
                      size="sm"
                      onClick={() => navigate('/command-center/products/catalog')}
                    >
                      Manage Products
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="h-100 border">
                <Card.Body className="d-flex align-items-center">
                  <div style={{ fontSize: '2rem', marginRight: '1rem' }}>🔄</div>
                  <div className="flex-grow-1">
                    <h6 className="mb-1">Upgrade Paths</h6>
                    <p className="text-muted small mb-2">Define how customers move through your ladder</p>
                    <Button
                      variant="outline-dark"
                      size="sm"
                      onClick={() => navigate('/command-center/products/upgrade-paths')}
                    >
                      Manage Paths
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Irresistible Offer Row */}
          <Row className="mt-3">
            <Col md={6}>
              <Card
                className="h-100 border"
                style={{
                  borderColor: hasOfferConfigured() ? colors.goldMid : undefined,
                  borderWidth: hasOfferConfigured() ? '2px' : undefined
                }}
              >
                <Card.Body className="d-flex align-items-center">
                  <div style={{ fontSize: '2rem', marginRight: '1rem' }}>💎</div>
                  <div className="flex-grow-1">
                    <h6 className="mb-1">
                      Irresistible Offer
                      {hasOfferConfigured() && (
                        <Badge bg="success" className="ms-2">Configured</Badge>
                      )}
                    </h6>
                    <p className="text-muted small mb-2">
                      Stack bonuses, guarantees, and urgency to make your offer impossible to refuse
                    </p>
                    <Button
                      variant={hasOfferConfigured() ? 'outline-dark' : 'outline-primary'}
                      size="sm"
                      onClick={() => { setShowOfferModal(true); fetchBonusLibrary(); }}
                    >
                      {hasOfferConfigured() ? 'Edit Offer' : 'Configure Offer'}
                    </Button>
                    {hasOfferConfigured() && (
                      <div className="mt-2 d-flex gap-3 flex-wrap align-items-center">
                        {offerForm.offer_name && (
                          <span className="fw-bold text-primary">
                            "{offerForm.offer_name}"
                          </span>
                        )}
                        {offerForm.offer_price && offerForm.regular_price && (
                          <Badge bg="success">
                            {offerForm.regular_price} → {offerForm.offer_price}
                          </Badge>
                        )}
                        {offerForm.bonuses.length > 0 && (
                          <Badge bg="light" text="dark" className="border">
                            🎁 {offerForm.bonuses.length} bonus{offerForm.bonuses.length !== 1 ? 'es' : ''}
                          </Badge>
                        )}
                        {offerForm.guarantee_type && (
                          <Badge bg="light" text="dark" className="border">
                            🛡️ {offerForm.guarantee_type.replace(/_/g, ' ')}
                          </Badge>
                        )}
                        {offerForm.urgency_type && (
                          <Badge bg="light" text="dark" className="border">
                            ⏰ {offerForm.urgency_type.replace(/_/g, ' ')}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Upsell Pages Section */}
      {products.length > 0 && (
        <Card className="shadow-sm mb-4">
          <Card.Header style={{ backgroundColor: colors.greyLightest }}>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Upsell & Cross-sell Pages</h5>
              <Badge bg="info">For Post-Purchase Sequences</Badge>
            </div>
          </Card.Header>
          <Card.Body>
            <p className="text-muted mb-3">
              Your post-purchase emails can link to these sales pages. Use master pages for general traffic,
              or create funnel-specific versions that reference "you just purchased X."
            </p>
            <Row>
              {products.filter(p => p.position !== 'tripwire' || !componentsByType['tripwire_page']?.some(c => c.name?.includes(p.name))).slice(0, 4).map(product => (
                <Col key={product.id} md={6} lg={3} className="mb-3">
                  <Card className="h-100 border">
                    <Card.Body className="text-center">
                      <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>
                        {product.position === 'premium' ? '👑' : product.position === 'backend' ? '🎯' : '📦'}
                      </div>
                      <h6 className="mb-1">{product.name}</h6>
                      <div className="small text-muted mb-2">${product.price}</div>
                      <div className="d-flex flex-column gap-1">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => navigate('/command-center/products/sales-pages')}
                        >
                          Master Page
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            setEditingComponent(null);
                            setComponentForm({
                              component_type: 'upsell_page',
                              name: `${product.name} Upsell - ${funnel?.name || 'Funnel'}`,
                              content: generateUpsellPageContent(product),
                              sequence_number: null,
                              widget_type: null,
                              widget_config: null
                            });
                            setShowComponentModal(true);
                          }}
                        >
                          Funnel-Specific
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
            {products.length > 4 && (
              <div className="text-center mt-2">
                <Button
                  variant="link"
                  onClick={() => navigate('/command-center/products/sales-pages')}
                >
                  View all {products.length} products →
                </Button>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Component Editor Modal */}
      <Modal show={showComponentModal} onHide={() => setShowComponentModal(false)} size="xl">
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <Modal.Title>
            {editingComponent ? 'Edit' : 'Create'} {componentTypes[componentForm.component_type]?.name || 'Component'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}

          <Form onSubmit={(e) => e.preventDefault()}>
            {/* Show widget info banner for lead_magnet_app */}
            {componentForm.component_type === 'lead_magnet_app' && componentForm.widget_type && (
              <Alert variant="info" className="mb-3">
                <div className="d-flex align-items-center">
                  <span style={{ fontSize: '1.5rem', marginRight: '0.75rem' }}>
                    {widgetTypes[componentForm.widget_type]?.icon || '🧮'}
                  </span>
                  <div>
                    <strong>{widgetTypes[componentForm.widget_type]?.name || 'Interactive Widget'}</strong>
                    <p className="mb-0 small">{widgetTypes[componentForm.widget_type]?.description}</p>
                  </div>
                </div>
              </Alert>
            )}

            <Row className="mb-3">
              <Col md={componentTypes[componentForm.component_type]?.is_sequence ? 8 : 12}>
                <Form.Group>
                  <Form.Label><strong>Name</strong></Form.Label>
                  <Form.Control
                    type="text"
                    value={componentForm.name}
                    onChange={(e) => setComponentForm({ ...componentForm, name: e.target.value })}
                  />
                </Form.Group>
              </Col>
              {componentTypes[componentForm.component_type]?.is_sequence && (
                <Col md={4}>
                  <Form.Group>
                    <Form.Label><strong>Sequence #</strong></Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={componentForm.sequence_number || ''}
                      onChange={(e) => setComponentForm({ ...componentForm, sequence_number: parseInt(e.target.value) || null })}
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>

            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0">
                  <strong>
                    {componentForm.component_type === 'lead_magnet_app' ? 'Widget Configuration' : 'Content'}
                  </strong>
                </Form.Label>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={generateContent}
                  disabled={generating}
                >
                  {generating ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      Generating...
                    </>
                  ) : (
                    '✨ Generate with AI'
                  )}
                </Button>
              </div>
              {componentForm.component_type === 'lead_magnet_app' ? (
                <>
                  {/* Widget description textarea */}
                  <Form.Control
                    as="textarea"
                    rows={widgetCode ? 4 : 12}
                    value={componentForm.content}
                    onChange={(e) => setComponentForm({ ...componentForm, content: e.target.value })}
                    style={{ fontFamily: 'monospace', fontSize: '13px' }}
                    placeholder="Describe what this widget should do, what inputs it needs, and what results it should show..."
                  />
                  <Form.Text className="text-muted mb-2 d-block">
                    Describe your widget's functionality. Use the "Generate Widget Code" button in the footer to create the actual widget.
                  </Form.Text>

                  {/* Generated widget code display */}
                  {widgetCode && (
                    <div className="mt-3">
                      <Form.Label><strong>Generated Widget Code</strong></Form.Label>
                      <div style={{
                        background: '#1e1e1e',
                        color: '#d4d4d4',
                        padding: '1rem',
                        borderRadius: '8px',
                        maxHeight: '200px',
                        overflow: 'auto',
                        fontFamily: 'monospace',
                        fontSize: '11px',
                        lineHeight: '1.5'
                      }}>
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                          {widgetCode.substring(0, 2000)}{widgetCode.length > 2000 ? '\n... (truncated for display)' : ''}
                        </pre>
                      </div>
                      <Form.Text className="text-muted">
                        {(widgetCode.length / 1024).toFixed(1)} KB standalone HTML — use "Preview Widget" to see it live, or "Copy HTML" to embed it.
                      </Form.Text>
                    </div>
                  )}
                </>
              ) : (
                /* Content pages use rich text editor */
                <RichTextEditor
                  key={editingComponent?.id || 'new'}
                  value={componentForm.content}
                  onChange={(content) => setComponentForm({ ...componentForm, content })}
                  placeholder="Enter your content here or click 'Generate with AI' to get started..."
                  height="400px"
                  toolbar="full"
                  mode="html"
                />
              )}
              {componentForm.component_type !== 'lead_magnet_app' && (
                <Form.Text className="text-muted">
                  Edit your content using the rich text editor. AI-generated content is ready for review and customization.
                </Form.Text>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          {editingComponent && (
            <Button
              variant="outline-danger"
              onClick={() => deleteComponent(editingComponent.id)}
            >
              Delete
            </Button>
          )}
          {componentForm.component_type === 'lead_magnet_app' ? (
            <>
              {SUPPORTED_WIDGET_TYPES.includes(componentForm.widget_type) ? (
                <Button
                  variant="outline-primary"
                  onClick={generateWidgetCode}
                  disabled={generatingWidget}
                >
                  {generatingWidget ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      Generating Widget...
                    </>
                  ) : widgetCode ? (
                    'Regenerate Widget Code'
                  ) : (
                    'Generate Widget Code'
                  )}
                </Button>
              ) : (
                <Button
                  variant="outline-primary"
                  disabled
                  title="Widget code generation coming soon for this type"
                >
                  Generate Widget Code (Coming Soon)
                </Button>
              )}
              <Button
                variant="outline-secondary"
                disabled={!widgetCode}
                onClick={() => setShowWidgetPreview(true)}
                title={widgetCode ? 'Preview the generated widget' : 'Generate widget code first'}
              >
                Preview Widget
              </Button>
              {widgetCode && (
                <Button
                  variant="outline-info"
                  onClick={() => {
                    navigator.clipboard.writeText(widgetCode);
                    setSuccess('Widget HTML copied to clipboard!');
                    setTimeout(() => setSuccess(''), 3000);
                  }}
                >
                  Copy HTML
                </Button>
              )}
            </>
          ) : (
            <Button
              variant="outline-secondary"
              onClick={() => {
                if (componentForm.content) {
                  exportComponent(editingComponent?.id || 'draft', componentForm.name);
                }
              }}
              disabled={!componentForm.content}
            >
              Export Text
            </Button>
          )}
          {editingComponent && ['lead_magnet_landing', 'tripwire_page', 'sales_page', 'upsell_page'].includes(componentForm.component_type) && (
            <Button
              variant="success"
              onClick={publishComponent}
              disabled={publishing || !componentForm.content}
              title={!componentForm.content ? 'Add content before publishing' : 'Publish to WordPress'}
            >
              {publishing ? 'Publishing...' : publishedUrl ? 'Republish' : 'Publish to WordPress'}
            </Button>
          )}
          <Button variant="secondary" onClick={() => setShowComponentModal(false)}>
            Cancel
          </Button>
          <Button
            variant="dark"
            style={{ backgroundColor: colors.greyDarkest }}
            onClick={saveComponent}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Lead Magnet Type Selector Modal */}
      <Modal show={showLeadMagnetSelector} onHide={() => setShowLeadMagnetSelector(false)} size="lg">
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <Modal.Title>
            {leadMagnetStep === 'format' && 'Choose Lead Magnet Format'}
            {leadMagnetStep === 'widget_type' && 'Select Widget Type'}
            {leadMagnetStep === 'widget_config' && `Configure ${widgetTypes[selectedWidgetType]?.name || 'Widget'}`}
            {leadMagnetStep === 'existing' && 'Link Your Existing Lead Magnet'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Step 1: Format Selection */}
          {leadMagnetStep === 'format' && (
            <Row>
              <Col md={4}>
                <Card
                  className="h-100 cursor-pointer border-2"
                  style={{ cursor: 'pointer' }}
                  onClick={() => selectLeadMagnetFormat('pdf')}
                >
                  <Card.Body className="text-center py-4">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
                    <h5>PDF / Document</h5>
                    <p className="text-muted small">
                      Traditional downloadable guide, checklist, or report.
                    </p>
                    <ul className="text-start text-muted small">
                      <li>Familiar format</li>
                      <li>Easy to share</li>
                      <li>Quick to implement</li>
                    </ul>
                    <Button variant="outline-dark" size="sm" className="mt-2">Select PDF</Button>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card
                  className="h-100 border-primary border-2"
                  style={{ cursor: 'pointer' }}
                  onClick={() => selectLeadMagnetFormat('app')}
                >
                  <Card.Body className="text-center py-4">
                    <Badge bg="primary" className="position-absolute" style={{ top: '10px', right: '10px' }}>Recommended</Badge>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🧮</div>
                    <h5>Interactive App</h5>
                    <p className="text-muted small">
                      Calculator, assessment, or tool visitors can use.
                    </p>
                    <ul className="text-start text-muted small">
                      <li>Higher engagement</li>
                      <li>Captures better data</li>
                      <li>Shows your expertise</li>
                    </ul>
                    <Button variant="primary" size="sm" className="mt-2">Select App</Button>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card
                  className="h-100 border-2"
                  style={{ cursor: 'pointer' }}
                  onClick={() => selectLeadMagnetFormat('existing')}
                >
                  <Card.Body className="text-center py-4">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔗</div>
                    <h5>I Already Have One</h5>
                    <p className="text-muted small">
                      Link your existing lead magnet from another platform.
                    </p>
                    <ul className="text-start text-muted small">
                      <li>Use what you have</li>
                      <li>Keep existing setup</li>
                      <li>We'll adapt the funnel</li>
                    </ul>
                    <Button variant="outline-dark" size="sm" className="mt-2">Link Existing</Button>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Step 2: Widget Type Selection */}
          {leadMagnetStep === 'widget_type' && (
            <>
              <p className="text-muted mb-4">
                Choose the type of interactive tool that best fits your business:
              </p>
              <Row>
                {Object.entries(widgetTypes).map(([type, info]) => (
                  <Col key={type} md={6} lg={4} className="mb-3">
                    <Card
                      className={`h-100 ${selectedWidgetType === type ? 'border-primary border-2' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => selectWidgetType(type)}
                    >
                      <Card.Body>
                        <div className="d-flex align-items-center mb-2">
                          <span style={{ fontSize: '1.5rem', marginRight: '0.5rem' }}>{info.icon}</span>
                          <strong>{info.name}</strong>
                        </div>
                        <p className="small text-muted mb-2">{info.description}</p>
                        <div className="d-flex flex-wrap gap-1">
                          {info.best_for?.slice(0, 2).map((tag, i) => (
                            <Badge key={i} bg="light" text="dark" className="border small">{tag}</Badge>
                          ))}
                        </div>
                        <div className="mt-2">
                          <small className="text-muted">Complexity: {info.complexity}</small>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
              <div className="mt-3">
                <Button variant="link" onClick={() => setLeadMagnetStep('format')}>
                  ← Back to format selection
                </Button>
              </div>
            </>
          )}

          {/* Step 3: Widget Configuration */}
          {leadMagnetStep === 'widget_config' && (
            <>
              <div className="mb-4 p-3 rounded" style={{ backgroundColor: colors.greyLightest }}>
                <div className="d-flex align-items-center mb-2">
                  <span style={{ fontSize: '1.5rem', marginRight: '0.5rem' }}>{widgetTypes[selectedWidgetType]?.icon}</span>
                  <strong>{widgetTypes[selectedWidgetType]?.name}</strong>
                </div>
                <p className="small text-muted mb-0">{widgetTypes[selectedWidgetType]?.description}</p>
              </div>

              <Form.Group className="mb-4">
                <Form.Label><strong>Describe Your Widget (Optional)</strong></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={widgetDescription}
                  onChange={(e) => setWidgetDescription(e.target.value)}
                  placeholder={`Example: "I want a ${widgetTypes[selectedWidgetType]?.name?.toLowerCase() || 'calculator'} that helps my clients understand how much they could save by switching to my service..."`}
                />
                <Form.Text className="text-muted">
                  Tell us what you want this widget to do, or leave blank and we'll suggest options.
                </Form.Text>
              </Form.Group>

              <div className="d-flex gap-3 mb-4">
                <Button
                  variant="primary"
                  onClick={generateWidgetOptions}
                  disabled={generatingOptions}
                >
                  {generatingOptions ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Generating Options...
                    </>
                  ) : (
                    '✨ Generate 3 Options'
                  )}
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={createCustomWidget}
                  disabled={!widgetDescription.trim()}
                >
                  Use My Description →
                </Button>
              </div>

              {/* Generated Options */}
              {generatedOptions.length > 0 && (
                <>
                  <h5 className="mb-3">Choose an Option:</h5>
                  <Row>
                    {generatedOptions.map((option, index) => (
                      <Col key={option.id || index} md={4} className="mb-3">
                        <Card
                          className="h-100"
                          style={{ cursor: 'pointer' }}
                          onClick={() => selectWidgetOption(option)}
                        >
                          <Card.Header style={{ backgroundColor: colors.greyLightest }}>
                            <strong>{option.title}</strong>
                          </Card.Header>
                          <Card.Body>
                            <p className="small text-muted mb-2">{option.description}</p>
                            <ul className="small mb-0">
                              {option.features?.map((feature, i) => (
                                <li key={i}>{feature}</li>
                              ))}
                            </ul>
                          </Card.Body>
                          <Card.Footer className="text-center">
                            <Button variant="outline-primary" size="sm">Select This</Button>
                          </Card.Footer>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </>
              )}

              <div className="mt-3">
                <Button variant="link" onClick={() => setLeadMagnetStep('widget_type')}>
                  ← Back to widget type
                </Button>
              </div>
            </>
          )}

          {/* Step: Existing Lead Magnet */}
          {leadMagnetStep === 'existing' && (
            <>
              <p className="text-muted mb-4">
                Select a lead magnet from your library or link an external one.
              </p>

              {/* Lead Magnet Selector */}
              {leadMagnets.length > 0 && (
                <Form.Group className="mb-4">
                  <Form.Label><strong>Select from Your Lead Magnets</strong></Form.Label>
                  <Form.Select
                    value={selectedLeadMagnetId}
                    onChange={(e) => setSelectedLeadMagnetId(e.target.value)}
                  >
                    <option value="">-- Choose a lead magnet --</option>
                    {leadMagnets.map(lm => (
                      <option key={lm.id} value={lm.id}>
                        {lm.name} {lm.lead_magnet_type ? `(${lm.lead_magnet_type})` : ''}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Choose from lead magnets you've created in the Products hub.
                  </Form.Text>
                </Form.Group>
              )}

              {leadMagnets.length === 0 && (
                <Alert variant="info" className="mb-4">
                  No lead magnets in your library yet.{' '}
                  <a href="/command-center/products/lead-magnets" target="_blank" rel="noopener noreferrer">
                    Create one in the Products hub
                  </a>{' '}
                  or link an external one below.
                </Alert>
              )}

              <div className="border-top pt-4 mt-3">
                <p className="text-muted mb-3">
                  <strong>Or link an external lead magnet:</strong>
                </p>
                <Form.Group className="mb-4">
                  <Form.Label>Lead Magnet URL</Form.Label>
                  <Form.Control
                    type="url"
                    placeholder="https://example.com/my-lead-magnet"
                    value={existingLeadMagnetForm.url}
                    onChange={(e) => setExistingLeadMagnetForm({ ...existingLeadMagnetForm, url: e.target.value })}
                  />
                  <Form.Text className="text-muted">
                    The URL where people sign up for or access your lead magnet (landing page, Gumroad, ConvertKit, etc.)
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>What's Your Lead Magnet About?</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Describe your lead magnet - what does it teach/provide? Who is it for? What problem does it solve?"
                    value={existingLeadMagnetForm.description}
                    onChange={(e) => setExistingLeadMagnetForm({ ...existingLeadMagnetForm, description: e.target.value })}
                  />
                  <Form.Text className="text-muted">
                    This helps us create aligned nurture emails and landing page content.
                  </Form.Text>
                </Form.Group>
              </div>

              {existingLeadMagnetForm.url && funnel?.existing_lead_magnet_url && (
                <Alert variant="info">
                  <strong>Currently linked:</strong> {funnel.existing_lead_magnet_url}
                </Alert>
              )}

              <div className="mt-3">
                <Button variant="link" onClick={() => setLeadMagnetStep('format')}>
                  ← Back to format selection
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {leadMagnetStep === 'existing' ? (
            <>
              <Button variant="secondary" onClick={() => setShowLeadMagnetSelector(false)}>
                Cancel
              </Button>
              <Button
                variant="dark"
                style={{ backgroundColor: colors.greyDarkest }}
                onClick={saveExistingLeadMagnet}
                disabled={savingExistingLeadMagnet || (!selectedLeadMagnetId && !existingLeadMagnetForm.url.trim())}
              >
                {savingExistingLeadMagnet ? 'Saving...' : 'Save Lead Magnet'}
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={() => setShowLeadMagnetSelector(false)}>
              Cancel
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Tripwire Product Selector Modal */}
      <Modal show={showTripwireSelector} onHide={() => setShowTripwireSelector(false)} size="lg">
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <Modal.Title>Create Tripwire Sales Page</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-4">
            Choose an existing product from your catalog to use as your tripwire offer, or create a new one from scratch.
          </p>

          {loadingProducts ? (
            <div className="text-center py-4">
              <Spinner animation="border" style={{ color: colors.goldMid }} />
              <p className="mt-2 text-muted">Loading your products...</p>
            </div>
          ) : (
            <>
              {/* Existing Products */}
              {products.length > 0 && (
                <>
                  <h5 className="mb-3">Choose from Your Products:</h5>
                  <Row className="mb-4">
                    {products.filter(p => p.price && parseFloat(p.price) < 100).length > 0 ? (
                      products.filter(p => p.price && parseFloat(p.price) < 100).map(product => (
                        <Col key={product.id} md={6} className="mb-3">
                          <Card
                            className="h-100"
                            style={{ cursor: 'pointer' }}
                            onClick={() => selectTripwireProduct(product)}
                          >
                            <Card.Body>
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <h6 className="mb-1">{product.name}</h6>
                                  <p className="small text-muted mb-2">{product.description?.substring(0, 80)}...</p>
                                </div>
                                <Badge bg="success" className="ms-2">${product.price}</Badge>
                              </div>
                              <Button variant="outline-primary" size="sm" className="mt-2">
                                Use This Product
                              </Button>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))
                    ) : (
                      <Col>
                        <Alert variant="info">
                          No low-ticket products found (under $100). You can create a new tripwire below or add products to your catalog first.
                        </Alert>
                      </Col>
                    )}
                  </Row>

                  {products.filter(p => !p.price || parseFloat(p.price) >= 100).length > 0 && (
                    <details className="mb-4">
                      <summary className="text-muted small" style={{ cursor: 'pointer' }}>
                        Show higher-priced products ({products.filter(p => !p.price || parseFloat(p.price) >= 100).length})
                      </summary>
                      <Row className="mt-3">
                        {products.filter(p => !p.price || parseFloat(p.price) >= 100).map(product => (
                          <Col key={product.id} md={6} className="mb-3">
                            <Card
                              className="h-100"
                              style={{ cursor: 'pointer', opacity: 0.7 }}
                              onClick={() => selectTripwireProduct(product)}
                            >
                              <Card.Body>
                                <div className="d-flex justify-content-between align-items-start">
                                  <div>
                                    <h6 className="mb-1">{product.name}</h6>
                                    <p className="small text-muted mb-2">{product.description?.substring(0, 80)}...</p>
                                  </div>
                                  <Badge bg="secondary" className="ms-2">${product.price || 'N/A'}</Badge>
                                </div>
                                <Button variant="outline-secondary" size="sm" className="mt-2">
                                  Use Anyway
                                </Button>
                              </Card.Body>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </details>
                  )}
                </>
              )}

              {/* No products message */}
              {products.length === 0 && (
                <Alert variant="warning" className="mb-4">
                  <strong>No products found!</strong> Add products to your catalog first, or create a new tripwire from scratch.
                  <Button
                    variant="link"
                    className="p-0 ms-2"
                    onClick={() => {
                      setShowTripwireSelector(false);
                      navigate('/command-center/products/catalog');
                    }}
                  >
                    Go to Product Catalog →
                  </Button>
                </Alert>
              )}

              {/* Create New Option */}
              <div className={products.length > 0 ? 'border-top pt-4' : ''}>
                <Card
                  style={{ cursor: 'pointer', borderStyle: 'dashed' }}
                  onClick={createNewTripwire}
                >
                  <Card.Body className="text-center py-4">
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✨</div>
                    <h5>Create New Tripwire</h5>
                    <p className="text-muted small mb-0">
                      Start from scratch with our sales page template
                    </p>
                  </Card.Body>
                </Card>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTripwireSelector(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Irresistible Offer Modal */}
      {/* Sales Page Pre-Generation Modal */}
      <Modal show={showSalesPagePreGen} onHide={() => setShowSalesPagePreGen(false)}>
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <Modal.Title style={{ fontSize: '1.1rem' }}>Sales Page Options</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#f4f4f4' }}>
          <p className="text-muted small mb-3">
            These optional inputs make your sales page more compelling. Skip any you don't have yet.
          </p>

          <Form.Group className="mb-3">
            <Form.Label>Sales Video URL <small className="text-muted">(optional)</small></Form.Label>
            <Form.Control
              type="url"
              placeholder="https://youtube.com/watch?v=... or Vimeo URL"
              value={salesPagePreGen.videoUrl}
              onChange={(e) => setSalesPagePreGen({ ...salesPagePreGen, videoUrl: e.target.value })}
            />
            <Form.Text className="text-muted">Video will be embedded in the hero section</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Testimonials</Form.Label>
            <Form.Select
              value={salesPagePreGen.testimonialSource}
              onChange={(e) => setSalesPagePreGen({ ...salesPagePreGen, testimonialSource: e.target.value })}
            >
              <option value="caseStudies">Use my case studies</option>
              <option value="manual">I'll paste testimonials</option>
              <option value="skip">Skip testimonials for now</option>
            </Form.Select>
          </Form.Group>

          {salesPagePreGen.testimonialSource === 'manual' && (
            <Form.Group className="mb-3">
              <Form.Label>Paste Your Testimonials</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder={'"This product changed everything..." - Jane D.\n"Best investment I\'ve made..." - John S.'}
                value={salesPagePreGen.manualTestimonials}
                onChange={(e) => setSalesPagePreGen({ ...salesPagePreGen, manualTestimonials: e.target.value })}
              />
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Common Objections <small className="text-muted">(optional)</small></Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="e.g., It's too expensive, I don't have time, Will this work for my industry?"
              value={salesPagePreGen.objections}
              onChange={(e) => setSalesPagePreGen({ ...salesPagePreGen, objections: e.target.value })}
            />
            <Form.Text className="text-muted">These become FAQ answers on the sales page</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>How Many Customers? <small className="text-muted">(optional)</small></Form.Label>
            <Form.Select
              value={salesPagePreGen.customerCount}
              onChange={(e) => setSalesPagePreGen({ ...salesPagePreGen, customerCount: e.target.value })}
            >
              <option value="">Skip</option>
              <option value="starting">Just starting out</option>
              <option value="under50">Under 50</option>
              <option value="50-100">50-100</option>
              <option value="100-500">100-500</option>
              <option value="500+">500+</option>
            </Form.Select>
            <Form.Text className="text-muted">Calibrates social proof language</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#f4f4f4' }}>
          <Button variant="outline-secondary" onClick={() => setShowSalesPagePreGen(false)}>
            Cancel
          </Button>
          <Button
            variant="dark"
            onClick={handleSalesPageGenerate}
            style={{ backgroundColor: colors.greyDarkest }}
          >
            Generate Sales Page
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showOfferModal} onHide={() => setShowOfferModal(false)} size="lg">
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <Modal.Title>💎 Irresistible Offer Configuration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-4">
            An irresistible offer combines a compelling deal with stacked value, risk reversal, and urgency.
            Define your core offer first, then add the elements that make it impossible to refuse.
          </p>

          {/* Core Offer Section */}
          <Card className="mb-4 border-primary">
            <Card.Header style={{ backgroundColor: colors.goldLight }}>
              <h6 className="mb-0">🎯 Core Offer (The Deal)</h6>
            </Card.Header>
            <Card.Body>
              <p className="text-muted small mb-3">
                What's the special deal for this funnel? This could be a bundle, a discount, a limited package -
                something that isn't just a standard product purchase.
              </p>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Offer Name</strong></Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g., Command Center Launch Bundle, Early Bird Special"
                      value={offerForm.offer_name || ''}
                      onChange={(e) => setOfferForm({ ...offerForm, offer_name: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Offer Type</strong></Form.Label>
                    <Form.Select
                      value={offerForm.offer_type || ''}
                      onChange={(e) => setOfferForm({ ...offerForm, offer_type: e.target.value })}
                    >
                      <option value="">Select type...</option>
                      <option value="bundle">Bundle (multiple items together)</option>
                      <option value="discount">Discount (% or $ off)</option>
                      <option value="extended_trial">Extended Trial/Access</option>
                      <option value="payment_plan">Special Payment Plan</option>
                      <option value="founding_member">Founding Member / Charter</option>
                      <option value="lifetime">Lifetime Access Deal</option>
                      <option value="custom">Custom Offer</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label><strong>What's Included</strong></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="e.g., 3 months of Command Center access, Course + all templates, Premium tier for 6 months..."
                  value={offerForm.offer_includes || ''}
                  onChange={(e) => setOfferForm({ ...offerForm, offer_includes: e.target.value })}
                />
              </Form.Group>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Regular Price</strong></Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g., $297, $99/mo"
                      value={offerForm.regular_price || ''}
                      onChange={(e) => setOfferForm({ ...offerForm, regular_price: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Offer Price</strong></Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g., $197, $67/mo"
                      value={offerForm.offer_price || ''}
                      onChange={(e) => setOfferForm({ ...offerForm, offer_price: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Savings</strong></Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g., 33% off, Save $100"
                      value={offerForm.offer_savings || ''}
                      onChange={(e) => setOfferForm({ ...offerForm, offer_savings: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Bonuses Section */}
          <Card className="mb-4">
            <Card.Header style={{ backgroundColor: colors.greyLightest }}>
              <h6 className="mb-0">🎁 Bonuses</h6>
            </Card.Header>
            <Card.Body>
              <p className="text-muted small mb-3">
                Add bonuses that increase perceived value without adding fulfillment cost. Digital products, templates, extra training, etc.
              </p>

              {/* Existing Bonuses */}
              {offerForm.bonuses.length > 0 && (
                <div className="mb-3">
                  {offerForm.bonuses.map((bonus, index) => (
                    <div key={index} className="d-flex align-items-center mb-2 p-2 border rounded">
                      <div className="flex-grow-1">
                        <strong>{bonus.name}</strong>
                        {bonus.value && <span className="text-success ms-2">(Value: ${bonus.value})</span>}
                        {bonus.description && <p className="small text-muted mb-0">{bonus.description}</p>}
                      </div>
                      <Button
                        variant="link"
                        className="text-danger p-0"
                        onClick={() => removeBonus(index)}
                      >
                        ✕
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Pick from Library */}
              {bonusLibrary.length > 0 && (
                <div className="mb-3">
                  <Button
                    variant="outline-dark"
                    size="sm"
                    onClick={() => setShowBonusPicker(!showBonusPicker)}
                    style={{ borderColor: colors.goldDark, color: colors.goldDark }}
                  >
                    {showBonusPicker ? 'Hide Library' : `Pick from Bonus Library (${bonusLibrary.length})`}
                  </Button>
                  {showBonusPicker && (
                    <div className="mt-2 p-2 border rounded" style={{ backgroundColor: '#fffbf0', maxHeight: '200px', overflowY: 'auto' }}>
                      {bonusLibrary.map((lb) => {
                        const alreadyAdded = offerForm.bonuses.some(b => b.name === lb.name);
                        return (
                          <div
                            key={lb.id}
                            className="d-flex align-items-center p-2 mb-1 rounded"
                            style={{
                              backgroundColor: alreadyAdded ? '#f0f0f0' : 'white',
                              border: `1px solid ${colors.greyLight}`,
                              cursor: alreadyAdded ? 'default' : 'pointer',
                              opacity: alreadyAdded ? 0.6 : 1,
                            }}
                            onClick={() => !alreadyAdded && addBonusFromLibrary(lb)}
                          >
                            <div className="flex-grow-1">
                              <strong>{lb.name}</strong>
                              {lb.perceived_value && <span className="text-success ms-2">(${lb.perceived_value})</span>}
                              {lb.description && <p className="small text-muted mb-0">{lb.description}</p>}
                            </div>
                            {alreadyAdded ? (
                              <Badge bg="secondary" className="ms-2">Added</Badge>
                            ) : (
                              <Button variant="outline-success" size="sm" className="ms-2">+ Add</Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Add New Bonus */}
              <small className="text-muted d-block mb-2">Or add a new bonus:</small>
              <Row className="g-2">
                <Col md={5}>
                  <Form.Control
                    type="text"
                    placeholder="Bonus name (e.g., Quick Start Guide)"
                    value={newBonus.name}
                    onChange={(e) => setNewBonus({ ...newBonus, name: e.target.value })}
                  />
                </Col>
                <Col md={3}>
                  <Form.Control
                    type="text"
                    placeholder="Value ($)"
                    value={newBonus.value}
                    onChange={(e) => setNewBonus({ ...newBonus, value: e.target.value })}
                  />
                </Col>
                <Col md={4}>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="text"
                      placeholder="Brief description"
                      value={newBonus.description}
                      onChange={(e) => setNewBonus({ ...newBonus, description: e.target.value })}
                    />
                    <Button
                      variant="outline-primary"
                      onClick={addBonus}
                      disabled={!newBonus.name.trim()}
                    >
                      +
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Guarantee Section */}
          <Card className="mb-4">
            <Card.Header style={{ backgroundColor: colors.greyLightest }}>
              <h6 className="mb-0">🛡️ Guarantee (Risk Reversal)</h6>
            </Card.Header>
            <Card.Body>
              <p className="text-muted small mb-3">
                A strong guarantee removes the fear of making a bad decision. The stronger the guarantee, the higher the conversion.
              </p>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Guarantee Type</strong></Form.Label>
                    <Form.Select
                      value={offerForm.guarantee_type}
                      onChange={(e) => setOfferForm({ ...offerForm, guarantee_type: e.target.value })}
                    >
                      <option value="">Select type...</option>
                      <option value="money_back_30">30-Day Money Back</option>
                      <option value="money_back_60">60-Day Money Back</option>
                      <option value="money_back_90">90-Day Money Back</option>
                      <option value="money_back_lifetime">Lifetime Money Back</option>
                      <option value="results_or_refund">Results or Refund</option>
                      <option value="double_money_back">Double Money Back</option>
                      <option value="satisfaction">Satisfaction Guarantee</option>
                      <option value="try_before_buy">Try Before You Buy</option>
                      <option value="custom">Custom Guarantee</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Guarantee Details</strong></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Describe your guarantee in your own words. What exactly do you promise? What's the process for claiming it?"
                      value={offerForm.guarantee_description}
                      onChange={(e) => setOfferForm({ ...offerForm, guarantee_description: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Urgency Section */}
          <Card className="mb-4">
            <Card.Header style={{ backgroundColor: colors.greyLightest }}>
              <h6 className="mb-0">⏰ Urgency & Scarcity</h6>
            </Card.Header>
            <Card.Body>
              <p className="text-muted small mb-3">
                Create a real reason for prospects to act now rather than "think about it." Use genuine urgency, never fake it.
              </p>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Urgency Type</strong></Form.Label>
                    <Form.Select
                      value={offerForm.urgency_type}
                      onChange={(e) => setOfferForm({ ...offerForm, urgency_type: e.target.value })}
                    >
                      <option value="">Select type...</option>
                      <option value="limited_time">Limited Time Offer</option>
                      <option value="limited_quantity">Limited Quantity</option>
                      <option value="fast_action_bonus">Fast Action Bonus</option>
                      <option value="price_increase">Price Increase Coming</option>
                      <option value="enrollment_closing">Enrollment Closing</option>
                      <option value="seasonal">Seasonal/Event-Based</option>
                      <option value="bonuses_expiring">Bonuses Expiring</option>
                      <option value="custom">Custom</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Urgency Details</strong></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Explain the urgency. What happens if they wait? When does the offer expire? Why is this urgent?"
                      value={offerForm.urgency_description}
                      onChange={(e) => setOfferForm({ ...offerForm, urgency_description: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Price Anchoring Section */}
          <Card>
            <Card.Header style={{ backgroundColor: colors.greyLightest }}>
              <h6 className="mb-0">💰 Price Anchoring</h6>
            </Card.Header>
            <Card.Body>
              <p className="text-muted small mb-3">
                Help prospects understand the true value compared to the price. What would they pay for this elsewhere? What's the cost of NOT having this?
              </p>
              <Form.Group>
                <Form.Label><strong>Value Stack Explanation</strong></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder="Example: The training alone is worth $497. The templates save you 20+ hours (worth $2,000 in billable time). The community access is $97/month everywhere else. Total value: $3,000+. Your investment today: just $297."
                  value={offerForm.price_anchoring}
                  onChange={(e) => setOfferForm({ ...offerForm, price_anchoring: e.target.value })}
                />
              </Form.Group>
            </Card.Body>
          </Card>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowOfferModal(false)}>
            Cancel
          </Button>
          <Button
            variant="dark"
            style={{ backgroundColor: colors.greyDarkest }}
            onClick={saveOffer}
            disabled={savingOffer}
          >
            {savingOffer ? 'Saving...' : 'Save Offer'}
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Widget Preview Modal */}
      <Modal
        show={showWidgetPreview}
        onHide={() => setShowWidgetPreview(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest }}>
          <Modal.Title style={{ color: colors.goldLight, fontFamily: "'Trajan Pro', serif", letterSpacing: '0.1em', fontSize: '1rem' }}>
            Widget Preview
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, background: '#f0f0f0' }}>
          {widgetCode ? (
            <iframe
              srcDoc={widgetCode}
              sandbox="allow-scripts"
              style={{
                width: '100%',
                height: '600px',
                border: 'none',
                borderRadius: '0 0 8px 8px'
              }}
              title="Widget Preview"
            />
          ) : (
            <div className="text-center p-5 text-muted">No widget code generated yet.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-info"
            onClick={() => {
              navigator.clipboard.writeText(widgetCode);
              setSuccess('Widget HTML copied to clipboard!');
              setTimeout(() => setSuccess(''), 3000);
            }}
          >
            Copy HTML
          </Button>
          <Button variant="secondary" onClick={() => setShowWidgetPreview(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default FunnelBuilder;
